import React, { useState, useMemo } from "react";
import { Row, Col, Button, Tag, Dropdown, Avatar } from "antd";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import {
  MoreOutlined,
  EyeOutlined,
  SettingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import MetricsGrid from "@/components/UI/Layout/MetricGrid";
import CustomerDetailsModal from "./modals/CustomerDetailsModal";
import { useCustomerData } from "./hooks/useCustomerData";
import PromoOfferModal from "./modals/PromoOfferModal";
import CreateCustomerModal from "./modals/CreateCustomerModal";

const CustomersManager = ({ tableTitle = "Customers" }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] =
    useState(false);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null); // Renamed from selectedOrder for clarity

  const [openOfferModal, setOpenOfferModal] = useState(false);
  const [selectedCustomerForPromo, setSelectedCustomerForPromo] =
    useState(null); // New state for customer data

  const openCustomerDetails = (customerData) => {
    setIsCustomerDetailsModalOpen(true);
    setSelectedCustomerDetails(customerData);
  };
  const closeCustomerDetails = () => {
    setIsCustomerDetailsModalOpen(false);
    setSelectedCustomerDetails(null); // Clear selected customer details
  };

  const handleOpenOfferModal = (customerData) => {
    setSelectedCustomerForPromo(customerData); // Set the customer to be offered a promo
    setOpenOfferModal(true);
  };

  const handleCloseOfferModal = () => {
    setOpenOfferModal(false);
    setSelectedCustomerForPromo(null); // Clear selected customer after closing
  };

  const {
    customersData,
    paginationInfo,
    summary,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading: customerLoading,
    isFetching,
  } = useCustomerData();

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSelectedRowKeys([]);
  };
  // console.log("customes Data", customersData);
  // Helper function to determine customer rank based on orders
  const getRankFromOrders = (orderCount) => {
    if (orderCount >= 20) return "Top Customer";
    if (orderCount >= 5) return "Active";
    return "Inactive";
  };

  // Transform API data to match table structure
  const tableData = useMemo(() => {
    if (!customersData || customersData.length === 0) return [];

    return customersData.map((customer) => ({
      key: customer.id.toString(),
      id: customer.id, // Ensure customer ID is available
      first_name: customer.first_name,
      last_name: customer.last_name,
      full_name: `${customer.first_name || ""} ${
        customer.last_name || ""
      }`.trim(),
      email: customer.email,
      phone_number: customer.phone,
      total_order: customer.orders_count,
      total_spent: customer.transactions_sum_amount?.toLocaleString() || "0",
      most_bought: customer.most_bought_service || "N/A",
      rank: customer.rank || getRankFromOrders(customer.orders_count),
      date_created: customer.created_at
        ? new Date(customer.created_at).toISOString().split("T")[0]
        : "N/A",
    }));
  }, [customersData]);

  // Calculate metrics from API data
  const metricData = useMemo(() => {
    const totalOrders = tableData.reduce((sum, c) => sum + c.total_order, 0);
    const allCustomers = summary?.total;
    return [
      {
        title: "All Customers",
        value: allCustomers,
        // change: "12.5",
        trend: "up",
      },
      {
        title: "New This Month",
        value: summary?.joined_this_month,
        // change: "12.5",
        trend: "up",
      },
      {
        title: "Total Orders",
        value: totalOrders.toString(),
        // change: "12.5",
        trend: "up",
      },
      {
        title: "Avg Orders/Customer",
        value: allCustomers > 0 ? (totalOrders / allCustomers).toFixed(1) : "0",
        // change: "20",
        trend: "up",
      },
    ];
  }, [summary, tableData]);

  // Filtered tableData based on ordrer count (for tabs)
  const activeCustomers = tableData.filter(
    (customer) => customer.total_order > 0 // Changed from orders_count to total_order
  );

  const inActiveCustomers = tableData.filter(
    (customer) => customer.total_order === 0 // Changed from orders_count to total_order
  );

  const topCustomers = tableData.filter(
    (customer) => customer.total_order > 5 // Changed from orders_count to total_order
  );

  const tableDataMap = {
    0: tableData,
    1: activeCustomers,
    2: inActiveCustomers,
    3: topCustomers,
  };

  // Status color mapping
  const getStatusColor = (rank) => {
    const statusColors = {
      Inactive: { color: "#F59E0B", backgroundColor: "#FEF3C7" },
      Active: { color: "#10B981", backgroundColor: "#D1FAE5" },
      "Top Customer": { color: "#3B82F6", backgroundColor: "#DBEAFE" },
    };
    return (
      statusColors[rank] || { color: "#6B7280", backgroundColor: "#F3F4F6" }
    );
  };

  // Action menu items
  const getActionMenuItems = (record) => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View Details",
      onClick: () => openCustomerDetails(record),
    },
    {
      key: "promo",
      icon: <SettingOutlined />,
      label: "Offer Promo",
      // Call handleOpenOfferModal with the specific customer's data
      onClick: () => handleOpenOfferModal(record),
    },
  ];

  // Table columns configuration
  const customerColumns = [
    {
      title: "Name",
      dataIndex: "full_name",
      key: "full_name",
      width: 180,
      render: (
        text,
        record // Pass record to get full customer data
      ) => (
        <div className="flex gap-2">
          <Avatar
            size={40}
            // You might want to use a customer.image_url if available
            // src={record.image_url || undefined}
            style={{
              backgroundColor: "#E8C300",
              fontSize: "18px",
              fontWeight: 500,
            }}
          >
            {text
              .split(" ")
              .map((name) => name[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </Avatar>
          <div className=" my-auto ">{text}</div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 150,
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 120,
    },
    {
      title: "Total Order",
      dataIndex: "total_order",
      key: "total_order",
      width: 120,
    },
    {
      title: "Total Spent",
      dataIndex: "total_spent",
      key: "total_spent",
      width: 120,
    },
    {
      title: "Most bought Service",
      dataIndex: "most_bought",
      key: "most_bought",
      width: 160,
    },
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      render: (rank) => {
        const { color, backgroundColor } = getStatusColor(rank);
        return (
          <Tag
            style={{
              color,
              backgroundColor,
              border: `1px solid ${color}20`,
              borderRadius: "8px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {rank}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getActionMenuItems(record),
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="text-gray-400 hover:text-gray-600"
          />
        </Dropdown>
      ),
    },
  ];

  // Updated tabs for customers
  const customerTabs = [
    { label: "All", count: paginationInfo.total },
    {
      label: "Active",
      count: activeCustomers.length,
    },
    {
      label: "Inactive",
      count: inActiveCustomers.length,
    },
    {
      label: "Top Customers",
      count: topCustomers.length,
    },
  ];

  return (
    <div className="px-6 bg-gray-50 min-h-full">
      <MetricsGrid data={metricData} />
      <Row>
        <Col span={24}>
          <CustomDataTable
            title={tableTitle}
            data={tableDataMap[activeTab] || tableData}
            columns={customerColumns}
            tabs={customerTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showSearch={true}
            searchPlaceholder="Search users..."
            searchValue={search}
            onServerSearch={handleSearch}
            searchMode="server"
            showFilter={false}
            showCSV={true}
            paginationMode="server"
            currentPage={paginationInfo.current_page}
            totalItems={paginationInfo.total}
            serverPageSize={paginationInfo.per_page}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={customerLoading || isFetching}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            selectedRowKeys={selectedRowKeys}
            actionButtons={[
              {
                label: "Create Customer",
                type: "primary",
                icon: <PlusOutlined />,
                className:
                  "bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:bg-gradient-to-l hover:from-[#F9A022] hover:to-[#D06D01] border-0",
                onClick: () => setIsCreateCustomerOpen(true),
              },
            ]}
          />
        </Col>
      </Row>
      <CreateCustomerModal
        open={isCreateCustomerOpen}
        onClose={() => setIsCreateCustomerOpen(false)}
        onSuccess={() => setIsCreateCustomerOpen(false)}
      />
      <CustomerDetailsModal
        open={isCustomerDetailsModalOpen}
        onClose={closeCustomerDetails}
        customerData={selectedCustomerDetails} // Pass selected customer details
        handleOpenOfferModal={() =>
          handleOpenOfferModal(selectedCustomerDetails)
        } // Pass a function that calls handleOpenOfferModal with the current selected customer
      />
      <PromoOfferModal
        open={openOfferModal}
        onClose={handleCloseOfferModal}
        customer={selectedCustomerForPromo} // Pass the selected customer data to the promo modal
      />
    </div>
  );
};

export default CustomersManager;
